import {setPriceLevels, setProducts, setBundles, setChildEvent} from '@/actions/event';
import { getSeriesConfig } from 'checkout/selectors/ui';
import { setListingItem, removeListingItem, getListingItem } from 'listing/helpers/storage';
import { validatePresaleCoupon } from '../../coupon/actions/coupon';
import { isValidDateObject, ISOStringToDate, dateToYMD } from '../../../../helpers/time';
import { setShowCalendarInModal, setCheckoutOverlay, setCurrentMonth } from 'checkout/actions/ui';
import { triggerTransition } from '@uirouter/redux';
import { getNumberOfMonths, hasActiveInventoryFilter, canSkipToCheckoutWithInventoryFilter, getSeriesOption } from '../selectors/eventSeries';
import { serializeToUrl } from '../../../helpers/helpers';
import { setLoading } from 'checkout/actions/loading';
import { createLoadingSelector } from 'checkout/selectors/helpers';
import { throttleQueryInventory, applyFiltersToTicketPicker } from './inventory-filter';
import { createReservation } from '../../../actions/reservation';
import { reservationActive } from '../../../selectors/reservation';
import { eventInFilterResults } from '../../../helpers/series';

export const SET_EVENT_TIMES = "SET_EVENT_TIMES";
export const SET_SELECTED_EVENT = "SET_SELECTED_EVENT";
export const SET_SELECTED_EVENT_TIME_OBJECT = "SET_SELECTED_EVENT_TIME_OBJECT";
export const SET_SELECTED_DATE = "SET_SELECTED_DATE";
export const SET_SELECTED_DATE_OBJECT = "SET_SELECTED_DATE_OBJECT";
export const SET_SELECTED_TIME = "SET_SELECTED_TIME";
export const SET_EVENTS_TODAY = "SET_EVENTS_TODAY";
export const SET_FETCHED_EVENTS_TODAY = "SET_FETCHED_EVENTS_TODAY";
export const SET_SERIES_EVENT_DATES = "SET_SERIES_EVENT_DATES";
export const SET_SERIES_SOLD_OUT_DATES = "SET_SERIES_SOLD_OUT_DATES";
export const SET_DATES_LAZY_LOADED = "SET_DATES_LAZY_LOADED";

export function setEventTimes(eventTimes, date = null) {
    return {type: SET_EVENT_TIMES, eventTimes, date};
}

export function setSelectedEvent(data) {
    return {type: SET_SELECTED_EVENT, data};
}

/**
 * Set the selected event time from state.eventSeries.eventTimes
 */
export function setSelectedEventTimeObject(data) {
    return {type: SET_SELECTED_EVENT_TIME_OBJECT, data};
}

export function setSelectedDate(date) {
    return (dispatch, getState) => {
        return new Promise((resolve, reject) => {
            if (isValidDateObject(date)) {
                let options = { year: 'numeric', month: 'long', day: 'numeric' };
                let dateString = date.toLocaleDateString("en-US", options);
                dispatch({type: SET_SELECTED_DATE_OBJECT, date});
                dispatch({type: SET_SELECTED_DATE, date:dateString});
                resolve(dateString);
            } else {
                dispatch({type: SET_SELECTED_DATE_OBJECT, date:null});
                dispatch({type: SET_SELECTED_DATE, date:''});
                resolve('');
            }
        });
    }
}

export function setSelectedTime(time) {
    return {type: SET_SELECTED_TIME, time};
}

export function clearEventTimes() {
    return {type: "CLEAR_EVENT_TIMES"};
}

export function setWidth(width) {
    return {type: "SET_WIDTH_FOR_SERIES_CALENDAR", width};
}

export function setSelectedChildEvent(id) {
    return (dispatch, getState) => {
        if(id === undefined) {
            removeListingItem(getState().ui.listingSlug, 'selectedEventId');
        } else {
            setListingItem(getState().ui.listingSlug, 'selectedEventId', id);
        }
        dispatch({type: "SET_SELECTED_CHILD_EVENT", id});
    }
}

export function setEventTimesForDate(date, eventTimes) {
    return {type: "SET_EVENT_TIME_FOR_DATE", date, eventTimes};
}

export function setEventTimesForDates(eventTimes) {
    return {type: "SET_EVENT_TIME_FOR_DATES", eventTimes};
}

export function getEventTimes(parentId, date) {
    return (dispatch, getState) => {
        let state = getState();
        const eventTimesLoading = createLoadingSelector("EVENT_TIMES");
        if(eventTimesLoading(state)) {
            return Promise.resolve();
        }

        dispatch(setLoading("EVENT_TIMES_REQUEST"));
        return fetchEventTimes(state.ui.apiUrl, state.ui.listingSlug, parentId, date).then((data) => {
            if (data) {
                dispatch(setLoading("EVENT_TIMES_SUCCESS"));
                dispatch(setEventTimes(data.eventTimes, date));
            } else {
                dispatch(setLoading("EVENT_TIMES_FAILURE"));
                Rollbar.critical("Empty response when fetching event times", JSON.stringify({
                    params: {url: state.ui.apiUrl, slug: state.ui.listingSlug, parentId, date},
                }));
            }
        }).catch(() => {
            dispatch(setLoading("EVENT_TIMES_FAILURE"));
        });
    }
}

/**
 * @param dates array of date objects
 */
export function getEventTimesForDates(dates) {
    return (dispatch, getState) => {
        let state = getState();
        const eventTimesLoading = createLoadingSelector("EVENT_TIMES");
        if(eventTimesLoading(state)) {
            return Promise.resolve();
        }
        dispatch(setLoading("EVENT_TIMES_REQUEST"));
        let parentId = Object.values(state.event.events)[0].event.event_id;
        if (!dates.length) return Promise.resolve();
        let sorted = dates.sort((a, b) => {
            if (isValidDateObject(a) && isValidDateObject(b)) {
                return a.getTime() - b.getTime();
            } else {
                return 1;
            }
        });
        let startDate = sorted[0];
        let endDate = sorted[sorted.length - 1];
        if (isValidDateObject(startDate)) startDate = dateToYMD(startDate);
        if (isValidDateObject(endDate)) endDate = dateToYMD(endDate);
        return fetchEventTimes(state.ui.apiUrl, state.ui.listingSlug, parentId, null, startDate, endDate).then((data) => {
            if (data) {
                dispatch(setLoading("EVENT_TIMES_SUCCESS"));
                dispatch(setEventTimesForDates(data.eventTimes));
            } else {
                dispatch(setLoading("EVENT_TIMES_FAILURE"));
                Rollbar.critical("Empty response when fetching event times", JSON.stringify({
                    params: {url: state.ui.apiUrl, slug: state.ui.listingSlug, parentId, startDate, endDate},
                }));
            }
        }).catch(() => {
            dispatch(setLoading("EVENT_TIMES_FAILURE"));
        });
    }
}

export function fetchEventTimes(apiUrl, listing, parentId, eventDate, startDate = null, endDate = null) {
    if(typeof eventDate === "object") {
        eventDate = dateToYMD(eventDate);
    }

    let payload = serializeToUrl({parentId, listing, eventDate, startDate, endDate});
    return fetch(apiUrl + '/event-series?' + payload, {
        method: 'GET',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        },
        credentials: 'same-origin',
    }).then((response) => {
        return response.json();
    }).catch((err) => {
    });
}

export function getEventSeriesChildListing(eventId) {
    return (dispatch, getState) => {
        let state = getState();
        let payload = serializeToUrl({eventId, listingId: state.ui.listingId});
        return fetch(state.ui.apiUrl + '/series-child?' + payload, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            credentials: 'same-origin',
        }).then((response) => {
            return response.json();
        }).then((data) => {
            if (data.childListingData) {
                let eventData = Object.values(data.childListingData.attributes.events)[0];
                dispatch(setChildEvent(eventData));
                dispatch(setSelectedEvent(eventData));
                dispatch(setPriceLevels(eventData.price_levels));
                if (eventData.products) dispatch(setProducts(eventData.products));
                if (eventData.bundles) {
                    dispatch(setBundles(eventData.bundles));
                }

                //handle the case where the user reloads after having entered a valid presale code for a series child.
                //In this scenario, we need to re-submit the code because reloading the page will have
                //caused all of the price levels, products, etc to be hidden again.
                const couponData = state.coupon;
                if (couponData.presaleCode && couponData.presaleCode !== "" && couponData.isPresaleValid && couponData.isPresaleCodeApplied) {
                    dispatch(validatePresaleCoupon(true));
                }
            }
        }).catch(function(err) {
            console.log(err);
        });
    }
}

//wrap the functionality for selecting a child event and
//continuing in the checkout process to eliminate duplication
export function selectChildEventAndContinue(eventId) {
    return (dispatch, getState) => {
        if (eventId) {
            let state = getState();

            setListingItem(state.ui.listingSlug, 'selectedEventId', eventId);
            dispatch(setLoading("SELECT_CHILD_EVENT_REQUEST"));
            dispatch(getEventSeriesChildListing(eventId)).then(() => {
                //get state again updated by getEventSeriesChildListing
                state = getState();

                // if skipping to checkout but you've selected invalid then you go to the ticketpicker like normal
                if (getSeriesOption('inventoryFilterSkipToCheckout', false)(state) && selectingFilteredEvent(eventId, state)) {
                    dispatch(applyFiltersToTicketPicker());
                    dispatch(setLoading("SELECT_CHILD_EVENT_SUCCESS"));
                    dispatch(triggerTransition("ticketPicker"));
                    return;
                }

                let childEventObject = (state.event.childEvent && state.event.childEvent.event) ? state.event.childEvent.event : null;
                if (childEventObject) {
                    dispatch(setCheckoutOverlay(true));
                    if (!childEventObject.isOnsale && childEventObject.hasPresale
                        && Object.keys(state.priceLevel.levels).length === 0
                        && state.coupon.isPresaleCodeApplied === false) {
                        // presale
                        dispatch(triggerTransition('coupon'));
                        dispatch(setLoading("SELECT_CHILD_EVENT_SUCCESS"));
                    } else {
                        if (hasActiveInventoryFilter(state)) {
                            let result = dispatch(applyFiltersToTicketPicker());
                            if (result && canSkipToCheckoutWithInventoryFilter(state)) {
                                dispatch(createReservation()).then(() => {
                                    if (!reservationActive(getState())) {
                                        dispatch(triggerTransition("ticketPicker"));
                                    }
                                }).finally(() => {
                                    dispatch(setLoading("SELECT_CHILD_EVENT_SUCCESS"));
                                });
                            } else {
                                dispatch(setLoading("SELECT_CHILD_EVENT_SUCCESS"));
                                dispatch(triggerTransition("ticketPicker"));
                            }
                        } else {
                            dispatch(setLoading("SELECT_CHILD_EVENT_SUCCESS"));
                            dispatch(triggerTransition("ticketPicker"));
                        }
                    }
                    dispatch(setShowCalendarInModal(true));
                } else {
                    dispatch(setLoading("SELECT_CHILD_EVENT_SUCCESS"));
                }
            });
        }
    }
}

function selectingFilteredEvent(eventId, state) {
    const filterResults = state.inventoryFilter.filterResults;
    if (hasActiveInventoryFilter(state) && filterResults !== null && !eventInFilterResults(+eventId, filterResults)) {
        return true;
    }
    return false;
}

export function setEventsToday(eventsToday) {
    return {type:SET_EVENTS_TODAY, value: eventsToday};
}

export function setFetchedEventsToday(value) {
    return {type:SET_FETCHED_EVENTS_TODAY, value};
}

export function setSeriesEventDates(dates) {
    return {type:SET_SERIES_EVENT_DATES, dates};
}

export function setSeriesSoldOutDates(dates) {
    return {type:SET_SERIES_SOLD_OUT_DATES, dates};
}

export function setDatesLazyLoaded(dates) {
    return {type:SET_DATES_LAZY_LOADED, dates};
}

/**
 * Lazy load series dates for the visible months based on the date provided
 *
 * @param month - date object passed to onMonthChange handlers by react-day-picker
 *                note: if we are displaying two months the date passed will reflect the first
 *                visible month (the one on the left).
 */
export function lazyLoadSeriesDates(month) {
    return (dispatch, getState) => {
        if (!isValidDateObject(month)) {
            console.log('Invalid date provided to load lazy load series children: ' + month);
            return Promise.resolve();
        }

        const state = getState();
        const numberMonths = getNumberOfMonths(state);
        const startDate = new Date(month.getFullYear(), month.getMonth(), 1, 0, 0, 0);
        const endDate = new Date(startDate.getFullYear(), startDate.getMonth() + numberMonths, 0, 23, 59, 59);
        const eventDates = [...Object.values(state.event.events)[0].event.series.eventDates];
        const soldOutDates = [...Object.values(state.event.events)[0].event.series.soldOutDates];
        const parentId = Object.values(state.event.events)[0].event.event_id;
        const datesLazyLoaded = [...state.eventSeries.datesLazyLoaded];

        if (createLoadingSelector('SERIES_CALENDAR')(state)) {
            return Promise.resolve();
        }

        if (isValidDateObject(startDate) && isValidDateObject(endDate) && startDate.getTime() <= endDate.getTime()) {
            let datesToFetch = [];
            let dateCursor = startDate;
            while (dateCursor.getTime() <= endDate.getTime()) {
                let ymd = dateToYMD(dateCursor);
                if (ymd && eventDates.indexOf(ymd) === -1 && soldOutDates.indexOf(ymd) === -1 && datesLazyLoaded.indexOf(ymd) === -1) {
                    datesToFetch.push(new Date(dateCursor.getTime()));
                }
                dateCursor = new Date(dateCursor.setDate(dateCursor.getDate() + 1));
            }
            if (datesToFetch.length) {
                let sorted = datesToFetch.sort((a, b) => {
                    return a.getTime() - b.getTime();
                });
                let startDateFilter = dateToYMD(sorted[0]);
                let endDateFilter = dateToYMD(sorted[sorted.length - 1]);
                if (startDateFilter && endDateFilter) {
                    dispatch(setLoading("SERIES_CALENDAR_REQUEST"));
                    return fetch(state.ui.apiUrl + '/series-dates/' + parentId + '?startDate=' + startDateFilter + '&endDate=' + endDateFilter, {
                        method: 'GET',
                        headers: {
                            'Accept': 'application/json',
                            'Content-Type': 'application/json'
                        },
                        credentials: 'same-origin',
                    }).then((response) => {
                        if (response.status === 200) {
                            return response.json();
                        }
                    }).then((response) => {
                        dispatch(setLoading("SERIES_CALENDAR_SUCCESS"));
                        if (response) {
                            if (Array.isArray(response.eventDates) && response.eventDates.length) {
                                let newEventDates = eventDates;
                                response.eventDates.map(eventDate => {
                                    if (newEventDates.indexOf(eventDate) === -1) {
                                        newEventDates.push(eventDate);
                                    }
                                });
                                newEventDates = newEventDates.sort();
                                dispatch(setSeriesEventDates(newEventDates));
                            }
                            if (Array.isArray(response.soldOutDates) && response.soldOutDates.length) {
                                let newSoldOutDates = soldOutDates;
                                response.soldOutDates.map(soldOutDate => {
                                    if (newSoldOutDates.indexOf(soldOutDate) === -1) {
                                        newSoldOutDates.push(soldOutDate);
                                    }
                                });
                                newSoldOutDates = newSoldOutDates.sort();
                                dispatch(setSeriesSoldOutDates(newSoldOutDates));
                            }
                            //update dates lazy loaded so we don't make this same request again
                            let newDatesLazyLoaded = datesLazyLoaded;
                            datesToFetch.map(date => {
                                let ymd = dateToYMD(date);
                                if (newDatesLazyLoaded.indexOf(ymd) === -1) {
                                    newDatesLazyLoaded.push(ymd);
                                }
                            });
                            dispatch(setDatesLazyLoaded(newDatesLazyLoaded));
                        }
                    }).catch((err) => {
                        dispatch(setLoading("SERIES_CALENDAR_FAILURE"));
                    });
                }
            }
        }
    }
}

/**
 * Track the current month and lazy load dates if necessary
 */
export function handleMonthChange(month) {
    return (dispatch, getState) => {
        dispatch(setCurrentMonth(isValidDateObject(month) && month.toISOString ? month.toISOString() : null));
        dispatch(lazyLoadSeriesDates(month));
        if(hasActiveInventoryFilter(getState())) {
            dispatch(throttleQueryInventory);
        }
    }
}
